import { FilterValues } from 'src/types/CataloguePage.types';
import { getOr } from 'src/utils/getOr';

import { FilterOption, FilterOptions } from './PlpFilters.type';
import { filterRangeFormatting } from './plpFiltersMap';

export const isInputSelectFilter = (filter: FilterOption) =>
  filter.filter === FilterValues.Ancap || filter.filter === FilterValues.Co2emissions;

export const isInputRangeFilter = (filter: FilterOption) =>
  [
    FilterValues.CashMax,
    FilterValues.CashMin,
    FilterValues.FinanceMax,
    FilterValues.FinanceMin,
    FilterValues.KMMax,
    FilterValues.KMMin,
    FilterValues.YearMax,
    FilterValues.YearMin,
    FilterValues.TowCapBrakedMin,
    FilterValues.TowCapBrakedMax,
  ].includes(filter.filter);

const isStateFilter = (filter: FilterOption) => filter.filter === FilterValues.State;

export const rangePairs: { [key: string]: string } = {
  kmMin: 'kmMax',
  kmMax: 'kmMin',
  cashMin: 'cashMax',
  cashMax: 'cashMin',
  financeMin: 'financeMax',
  financeMax: 'financeMin',
  yearMin: 'yearMax',
  yearMax: 'yearMin',
  towCapBrakedMin: 'towCapBrakedMax',
  towCapBrakedMax: 'towCapBrakedMin',
};

const hierarchyPairs: { [key: string]: string } = {
  make: 'model',
  model: 'make',
  bodyType: 'bodySize',
  bodySize: 'bodyType',
};

export const getHierarchyFilterGroupSelectedMap = (currentFilters: FilterOption[], selectedFilter: FilterOption) => {
  const groupMap = new Map();
  const filterGroupObject = currentFilters.filter(
    (filter) =>
      hierarchyPairs[filter.filter] === selectedFilter.filter &&
      (filter.slug.includes(selectedFilter.slug) || selectedFilter.slug.includes(filter.slug)),
  );
  filterGroupObject.forEach((filter) => {
    groupMap.set(filter.value, filter.filter);
  });
  return { hasExistingHierarchyFilterGroup: !!filterGroupObject.length, existingHierarchyFilterGroup: groupMap };
};

const isRangePairFilter = (currentFilters: FilterOption[], selectedFilter: FilterOption) => {
  return currentFilters.some(
    (filter) => rangePairs[filter.filter] && rangePairs[filter.filter] === selectedFilter.filter,
  );
};

const combineRangeFilter = (minRangeFilter: FilterOption, maxRangeFilter: FilterOption): FilterOption => {
  const combinedValue = `${minRangeFilter.value}-${maxRangeFilter.value}`;
  const combinedFilterValue = filterRangeFormatting.find((item) => item.min === minRangeFilter.filter)?.filterName;
  const combinedSlug = `${combinedFilterValue}-${minRangeFilter.value}-${maxRangeFilter.value}`;
  const combinedFilter: FilterOption = {
    filter: minRangeFilter.filter,
    value: combinedValue,
    slug: combinedSlug,
  };
  return combinedFilter;
};

export const handleRangeFilter = (currentFilters: FilterOption[], selectedFilter: FilterOption) => {
  let updatedFilters = currentFilters;
  const isFilterSelected = currentFilters.some((filter) => filter.filter === selectedFilter.filter);
  if (isFilterSelected) {
    updatedFilters = currentFilters.filter(
      (filter) => filter.filter !== selectedFilter.filter && filter.filter !== rangePairs[selectedFilter.filter],
    );
    updatedFilters = [...updatedFilters, selectedFilter];
  } else if (isRangePairFilter(currentFilters, selectedFilter)) {
    const minRangeFilter =
      currentFilters.find(
        (filter) => filter.filter === rangePairs[selectedFilter.filter] && filter.filter.includes('Min'),
      ) ?? selectedFilter;
    const maxRangeFilter = selectedFilter.filter.includes('Max')
      ? selectedFilter
      : currentFilters.find((filter) => filter.filter === rangePairs[selectedFilter.filter])!;
    const updatedRangeFilter = combineRangeFilter(minRangeFilter, maxRangeFilter);
    updatedFilters = currentFilters.filter((filter) => rangePairs[filter.filter] !== selectedFilter.filter);
    updatedFilters = [...updatedFilters, updatedRangeFilter];
  } else {
    updatedFilters = [...updatedFilters, selectedFilter];
  }
  return updatedFilters;
};

export const handleHierarchyFilter = (
  currentFilters: FilterOption[],
  selectedFilter: FilterOption,
  existingHierarchyFilterGroup: Map<string, string>,
) => {
  return [...currentFilters.filter((filter) => !existingHierarchyFilterGroup.has(filter.value)), selectedFilter];
};

export const handleInputSelectFilter = (currentFilters: FilterOption[], selectedFilter: FilterOption) => {
  let updatedFilters = currentFilters.filter((filter) => filter.filter !== selectedFilter.filter);
  if (selectedFilter.value) {
    updatedFilters = [...updatedFilters, selectedFilter];
  }
  return updatedFilters;
};

export const handleDefaultFilter = (currentFilters: FilterOption[], selectedFilter: FilterOption) => {
  const isFilterSelected = currentFilters.some(
    (filter) => filter.filter === selectedFilter.filter && filter.value === selectedFilter.value,
  );
  if (isFilterSelected) {
    return currentFilters.filter((filter) => filter.slug !== selectedFilter.slug);
  } else {
    return [...currentFilters, selectedFilter];
  }
};

const handleStateFilter = (currentFilters: FilterOption[], selectedFilter: FilterOption) => {
  const isFilterSelected = currentFilters.some((filter) => filter.filter === selectedFilter.filter);
  if (isFilterSelected) {
    return currentFilters.filter((filter) => filter.filter !== FilterValues.State);
  } else {
    return [...currentFilters, selectedFilter];
  }
};

export const updateCurrentFilterOptions = (currentFilters: FilterOption[], selectedFilter: FilterOption) => {
  const { existingHierarchyFilterGroup, hasExistingHierarchyFilterGroup } = getHierarchyFilterGroupSelectedMap(
    currentFilters,
    selectedFilter,
  );
  let updatedFilters;
  if (isStateFilter(selectedFilter)) {
    updatedFilters = handleStateFilter(currentFilters, selectedFilter);
  } else if (isInputRangeFilter(selectedFilter)) {
    updatedFilters = handleRangeFilter(currentFilters, selectedFilter);
  } else if (isInputSelectFilter(selectedFilter)) {
    updatedFilters = handleInputSelectFilter(currentFilters, selectedFilter);
  } else if (hasExistingHierarchyFilterGroup) {
    updatedFilters = handleHierarchyFilter(currentFilters, selectedFilter, existingHierarchyFilterGroup);
  } else {
    updatedFilters = handleDefaultFilter(currentFilters, selectedFilter);
  }
  return updatedFilters;
};

export const extractFilterOptions = (filterOptions: FilterOptions, currentFilters: FilterOption[]) => {
  return {
    makeModelFilters: {
      hasFilter: currentFilters.some((filter) => filter.filter === 'make' || filter.filter === 'model'),
      options: getOr(filterOptions, 'makeModel', []),
    },
    bodyTypeFilters: {
      hasFilter: currentFilters.some((filter) => filter.filter === 'bodyType' || filter.filter === 'bodySize'),
      options: getOr(filterOptions, 'bodyType', []),
    },
    priceFilters: {
      hasFilter: currentFilters.some((filter) =>
        ['cashMin', 'cashMax', 'financeMin', 'financeMax'].includes(filter.filter),
      ),
      options: {
        minCashOptions: getOr(filterOptions, 'cashMin', [] as FilterOption[]),
        maxCashOptions: getOr(filterOptions, 'cashMax', [] as FilterOption[]),
        minFinanceOptions: getOr(filterOptions, 'financeMin', [] as FilterOption[]),
        maxFinanceOptions: getOr(filterOptions, 'financeMax', [] as FilterOption[]),
      },
    },
    yearFilters: {
      hasFilter: currentFilters.some((filter) => ['yearMin', 'yearMax'].includes(filter.filter)),
      options: {
        minOptions: getOr(filterOptions, 'yearMin', []).map((option) => ({
          ...option,
          label: option.label === 'All' ? 'From' : option.label,
        })),
        maxOptions: getOr(filterOptions, 'yearMax', []).map((option) => ({
          ...option,
          label: option.label === 'All' ? 'To' : option.label,
        })),
      },
      minValue: currentFilters.find((filter) => filter.filter === 'yearMin')?.value ?? '',
      maxValue: currentFilters.find((filter) => filter.filter === 'yearMax')?.value ?? '',
    },
    kilometersFilters: {
      hasFilter: currentFilters.some((filter) => ['kmMin', 'kmMax'].includes(filter.filter)),
      options: {
        minOptions: getOr(filterOptions, 'kmMin', []).map((option) => ({
          ...option,
          label: option.label === 'All' ? 'From' : option.label,
        })),
        maxOptions: getOr(filterOptions, 'kmMax', []).map((option) => ({
          ...option,
          label: option.label === 'All' ? 'To' : option.label,
        })),
      },
      minValue: currentFilters.find((filter) => filter.filter === 'kmMin')?.value ?? '',
      maxValue: currentFilters.find((filter) => filter.filter === 'kmMax')?.value ?? '',
    },
    fuelTypeFilters: {
      hasFilter: currentFilters.some((filter) => filter.filter === 'fuelType'),
      options: getOr(filterOptions, 'fuelType', []),
    },
    highlightedFeaturesFilters: {
      hasFilter: currentFilters.some((filter) => filter.filter === 'highlightedFeatures'),
      options: getOr(filterOptions, 'highlightedFeatures', []),
    },
    transmissionFilters: {
      hasFilter: currentFilters.some((filter) => filter.filter === 'transmission'),
      options: getOr(filterOptions, 'transmission', []),
    },
    driveTypeFilters: {
      hasFilter: currentFilters.some((filter) => filter.filter === 'driveType'),
      options: getOr(filterOptions, 'driveType', []),
    },
    seatsFilters: {
      hasFilter: currentFilters.some((filter) => filter.filter === 'seats'),
      options: getOr(filterOptions, 'seats', []),
    },
    doorsFilters: {
      hasFilter: currentFilters.some((filter) => filter.filter === 'doors'),
      options: getOr(filterOptions, 'doors', []),
    },
    colourFilters: {
      hasFilter: currentFilters.some((filter) => filter.filter === 'colour'),
      options: getOr(filterOptions, 'colour', []),
    },
    ancapFilters: {
      hasFilter: currentFilters.some((filter) => filter.filter === 'ancap'),
      options: getOr(filterOptions, 'ancapSafetyRating', []),
      value: currentFilters.find((filter) => filter.filter === 'ancap')?.value ?? '',
    },
    co2Filters: {
      hasFilter: currentFilters.some((filter) => filter.filter === 'co2emissions'),
      options: getOr(filterOptions, 'co2Emissions', []),
      value: currentFilters.find((filter) => filter.filter === 'co2emissions')?.value ?? '',
    },
    towCapBrakedFilters: {
      hasFilter: currentFilters.some((filter) => ['towCapBrakedMin', 'towCapBrakedMax'].includes(filter.filter)),
      options: {
        minOptions: getOr(filterOptions, 'towCapBrakedMin', []).map((option) => ({
          ...option,
          label: option.label === 'All' ? 'From' : option.label,
        })),
        maxOptions: getOr(filterOptions, 'towCapBrakedMax', []).map((option) => ({
          ...option,
          label: option.label === 'All' ? 'To' : option.label,
        })),
      },
      minValue: currentFilters.find((filter) => filter.filter === 'towCapBrakedMin')?.value ?? '',
      maxValue: currentFilters.find((filter) => filter.filter === 'towCapBrakedMax')?.value ?? '',
    },
    stateValue: currentFilters.find((filter) => filter.filter === 'state')?.value ?? '',
  };
};
